import React, { ComponentProps, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Archive, ChevronLeft, Cross, Download, Hammer, type LucideIcon, Trash2, X } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Loader } from '@ui';

import { TabBar } from '@app/components/core/tab-bar';
import { togglerTabButtonClass } from '@app/components/core/toggler-tab-button-class';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { useBackNavigation } from '@app/components/core/useBackNavigation';
import { pageTitleClass, tabClass } from '@app/constants/styles';
import { isProductIncidentsEnabled } from '@app/constants/featureFlags';
import { clientsReportingPath, type ProductPageTab, productTabPath, productsPath } from '@app/constants/url';
import { capitalize } from '@app/utils';
import { useOrganizationId } from '@app/utils/hooks';
import {
  Chapter,
  ClientAuditTabValue,
  ProductQualityAudit,
  ProductProductivityReport,
  ProductStatus,
  SprintProductStatus,
  useClientOwnProducts,
  useSprintProductsForViewer,
} from '@data';
import { useOrganizationChapters } from '@data/hooks/organization';
import {
  useDeleteProduct,
  useProduct,
  useProductAuditReports,
  useProductProductivityReports,
  useProductQualityAudits,
} from '@data/hooks/products';

import ClientAccessCard from './ClientAccessCard';
import ConnectedApps from './ConnectedApps';
import ProductDetailsCard from './ProductDetailsCard';
import RndDossierCard from './RndDossierCard';
import CostsTab from './CostsTab';
import InsightsTab from './InsightsTab';
import TeamAndRatesTab from './TeamAndRatesTab';
import IncidentsTab, { IncidentPeriodSelect } from '../../clientReporting/product/IncidentsTab';
import { IncidentPeriod, defaultIncidentPeriod } from '../../clientReporting/product/sources/incidents';
import ProductDashboard, { HealthSection } from '../../clientReporting/product/ProductDashboard';
import QualityTab, { QualityPeriodSelect } from '../../clientReporting/product/QualityTab';
import { qualityPeriodLabel } from '../../clientReporting/product/sources/quality';
import AuditReportTab from '../../clientReporting/product/AuditReportTab';
import ProductivityTab from '../../clientReporting/product/ProductivityTab';
import ExportDialog from '../../clientReporting/product/ExportDialog';
import { isProductFinished } from '../../clientReporting/product/status';
import SprintProducts from '../../clientReporting/sprintProducts';
import { isSprintVisited } from '../../clientReporting/visitedSprints';
import { InternalReaderProvider } from '../../clientReporting/internalReader';
import UnsavedWorkProvider, { useUnsavedWork } from './unsavedWork';
import { useProductVersions } from './useProductVersions';
import { useVersionTeams } from './useVersionTeams';
import { useYearSpend } from './yearSpend';
import type { VersionTeam } from './budget';
import { useProductStore } from './useProductStore';
import { ProductViewerProvider } from './productViewer';
import RemoveProductDialog from './RemoveProductDialog';

export type ProductTab = ProductPageTab;

// A symbol per state: tools while active, a health cross for support, an X for deactivated, a box for archived.
const STATUS_ICON: Record<string, LucideIcon> = {
  [ProductStatus.active]: Hammer,
  [ProductStatus.support]: Cross,
  [ProductStatus.deactivated]: X,
  [ProductStatus.archived]: Archive,
};

const SENT = [SprintProductStatus.closed, SprintProductStatus.approved, SprintProductStatus.invoiced];

/** The client's reports, read by one of us as the client reads them: the client-scoped endpoints refuse us otherwise. */
const AsClientReads = ({ internal, children }: { internal: boolean; children: ReactNode }): ReactElement =>
  internal ? <InternalReaderProvider>{children}</InternalReaderProvider> : <>{children}</>;

/** Its own component, so the year's reports are only read while the dashboard is open. */
const DashboardWithSpend = ({
  teams,
  chapters,
  ...props
}: { teams: VersionTeam[]; chapters: Chapter[] } & Omit<
  ComponentProps<typeof ProductDashboard>,
  'spend'
>): ReactElement => {
  const spend = useYearSpend(teams, chapters, !props.finished);
  return <ProductDashboard {...props} spend={spend} />;
};

/** The three audits share one tab, each still at its own address so old links keep working. */
const AUDIT_TABS: ProductTab[] = ['quality', 'security-audit', 'productivity'];

/** The tabs that read the product's store: rates, budgets and costs. The dashboard copes without it on its own. */
const MONEY_TABS: ProductTab[] = ['team', 'costs'];

function ProductDetailLoaded({
  organizationId,
  tab,
  asClient,
  storeFailed,
}: {
  organizationId: string;
  tab: ProductTab;
  asClient: boolean;
  storeFailed: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { productId = '' } = useParams<{ productId: string }>();
  const transitionNavigate = useViewTransitionNav();
  const location = useLocation();
  const navigate = useNavigate();
  const { guard } = useUnsavedWork();
  // The routes already decided who reads: products is ours, the client portal is theirs.
  const canEdit = !asClient;
  const { label: backLabel, goBack } = useBackNavigation(
    asClient ? clientsReportingPath(organizationId) : productsPath(organizationId),
    t('actions.backToProducts'),
  );

  const remove = useDeleteProduct();
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [removeFailed, setRemoveFailed] = useState<string | null>(null);
  const [incidentPeriod, setIncidentPeriod] = useState<IncidentPeriod>(() => defaultIncidentPeriod());
  const [qualityReport, setQualityReport] = useState<ProductQualityAudit | null>(null);
  const [productivityReport, setProductivityReport] = useState<ProductProductivityReport | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const { data: product, isLoading } = useProduct(productId);
  // Each tab holds its own hook over the same store, so opening one is when another's writes land.
  const versions = useProductVersions(productId, tab);
  const {
    data: chapterData,
    isPending: chaptersPending,
    isError: chaptersFailed,
  } = useOrganizationChapters(organizationId, { includeDisabled: true });
  const chapters = useMemo(() => chapterData?.items ?? [], [chapterData]);
  // Every version's team at once: each sprint is priced under the version covering it.
  const teams = useVersionTeams(product, versions.versions, `${tab}:${versions.revision}`);

  // Which other products this client has decides whether there is a list to go back to.
  const { data: ownProducts } = useClientOwnProducts(asClient ? organizationId : undefined);
  const showBack = !asClient || (ownProducts?.items.length ?? 0) > 1;

  // Nothing records what the client has opened, so the "N new" hint is the client's alone.
  const { data: sentReports } = useSprintProductsForViewer(false, asClient ? organizationId : undefined, {
    statuses: SENT,
    productId,
  });
  const newCount = (sentReports?.items ?? []).filter(
    item => item.product?.id === productId && !isSprintVisited(productId, item.sprint?.id),
  ).length;

  // No audit, no tab, and a read Phill cannot complete counts as no audit.
  const { data: qualityAudits, isLoading: auditsLoading, error: auditsError } = useProductQualityAudits(productId);
  // The API refuses with this when it could not read GitHub, which is not the same answer as "no audit".
  const auditsUnavailable =
    (auditsError as { response?: { data?: { reason?: string } } })?.response?.data?.reason === 'githubUnreachable';
  const audits = useMemo(() => qualityAudits?.items ?? [], [qualityAudits]);
  // The productivity and security reports follow the same rule: a tab for the client only once there is one.
  const { data: auditReports, isLoading: reportsLoading, error: reportsError } = useProductAuditReports(productId);
  const reportsUnavailable =
    (reportsError as { response?: { data?: { reason?: string } } })?.response?.data?.reason === 'githubUnreachable';
  const reportOf = (kind: 'productivity' | 'security') => auditReports?.items.find(each => each.kind === kind);
  const {
    data: productivityIndex,
    isLoading: productivityLoading,
    error: productivityError,
  } = useProductProductivityReports(productId);
  const productivityUnavailable =
    (productivityError as { response?: { data?: { reason?: string } } })?.response?.data?.reason ===
    'githubUnreachable';
  const productivityReports = useMemo(() => productivityIndex?.items ?? [], [productivityIndex]);
  // Open on the most recent audit, and follow the product: another product must not inherit this one's quarter.
  useEffect(() => {
    setQualityReport(audits[0] ?? null);
  }, [productId, audits[0]?.id]);
  useEffect(() => {
    setProductivityReport(productivityReports[0] ?? null);
  }, [productId, productivityReports[0]?.id]);

  if (isLoading || !product) return <Loader />;

  // The two audits, then the productivity report. Always ours to open, audited or not; a client sees each
  // only once the product shares it and there is something committed to read.
  // Fails closed: a product read without the list shares nothing.
  const shared = (kind: ClientAuditTabValue): boolean => (product.clientAuditTabs ?? []).includes(kind);
  const auditTabs: { value: ProductTab; label: string; shown: boolean }[] = [
    {
      value: 'quality',
      label: t('tabs.qualityAudit'),
      shown: !asClient || (shared('quality') && (audits.length > 0 || auditsUnavailable)),
    },
    {
      value: 'security-audit',
      label: t('tabs.securityAudit'),
      shown: !asClient || (shared('security') && (!!reportOf('security') || reportsUnavailable)),
    },
    {
      value: 'productivity',
      label: t('tabs.productivity'),
      shown:
        !asClient ||
        (shared('productivity') &&
          (!!reportOf('productivity') ||
            reportsUnavailable ||
            productivityUnavailable ||
            productivityReports.length > 0)),
    },
  ];
  const shownAudits = auditTabs.filter(each => each.shown);
  const onAudit = AUDIT_TABS.includes(tab);
  const tabs: { value: ProductTab; label: ReactNode; shown: boolean }[] = [
    { value: 'dashboard', label: t('tabs.dashboard'), shown: true },
    { value: 'monitoring', label: t('tabs.monitoring'), shown: true },
    {
      value: 'sprints',
      label: (
        <>
          {t('tabs.sprints')}
          {newCount > 0 && (
            <span className="ml-2 rounded-md border border-zinc-300 px-2 py-0.5 text-xs font-bold text-zinc-500 dark:border-zinc-600 dark:text-zinc-400">
              {t('nNew', { count: newCount })}
            </span>
          )}
        </>
      ),
      shown: true,
    },
    { value: 'team', label: t('tabs.productTeamRates'), shown: true },
    // Every product, agreement or not: an outage on a product nobody signed for still belongs on its page.
    { value: 'incidents', label: t('incidents.title'), shown: isProductIncidentsEnabled() },
    { value: 'costs', label: t('tabs.costs'), shown: true },
    // Stays on the audit already open, otherwise opens the first one there is.
    {
      value: onAudit ? tab : (shownAudits[0]?.value ?? 'quality'),
      label: t('tabs.audit'),
      shown: shownAudits.length > 0,
    },
    { value: 'settings', label: t('tabs.settings'), shown: true },
  ];
  const pathOf = (value: ProductTab): string => productTabPath(organizationId, product.id, value, asClient);

  // A hidden tab must not stay reachable by URL; a client's audit tabs wait for their read, so a refresh does not bounce off them.
  const hidden = [...tabs, ...auditTabs].find(each => each.value === tab && !each.shown);
  // An unreadable GitHub keeps the quality tab too: bouncing off a bookmarked audit would say it is gone on no evidence.
  const stillReading =
    (tab === 'quality' && auditsLoading) ||
    ((tab === 'productivity' || tab === 'security-audit') && (reportsLoading || reportsUnavailable)) ||
    (tab === 'productivity' && (productivityLoading || productivityUnavailable));
  // An audit the product does not share is settled already, whatever is still loading.
  const unshared = asClient && onAudit && !shared(tab === 'security-audit' ? 'security' : (tab as ClientAuditTabValue));
  if (hidden && (unshared || !stillReading)) {
    return <Navigate to={pathOf('dashboard')} replace state={location.state} />;
  }

  // A tab replaces the current entry, so back leaves the product rather than walking through its tabs.
  const switchTab = (e: React.MouseEvent, to: string, active: boolean): void => {
    if (active || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    guard(() => transitionNavigate(to, 'fade', { replace: true, state: location.state }));
  };

  // One title over all three audits, with the switch beside it; with nothing to pick, each keeps its own heading.
  const auditHeader =
    shownAudits.length > 1 ? (
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            {t('auditReports.title')}
            {/* The picker names the audit on screen but is hidden in print, so the PDF says it here. */}
            <span className="hidden print:inline">
              {` · ${shownAudits.find(each => each.value === tab)?.label ?? ''}`}
              {tab === 'quality' && qualityReport ? ` · ${qualityPeriodLabel(qualityReport)}` : ''}
              {tab === 'productivity' && productivityReport ? ` · ${qualityPeriodLabel(productivityReport)}` : ''}
            </span>
          </h2>
          <p className="text-sm text-muted-foreground print:hidden">{t('auditReports.hint')}</p>
        </div>
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-3 print:hidden">
          {tab === 'quality' && (
            <QualityPeriodSelect value={qualityReport} onChange={setQualityReport} audits={audits} />
          )}
          {tab === 'productivity' && (
            <QualityPeriodSelect
              value={productivityReport}
              onChange={setProductivityReport}
              audits={productivityReports}
            />
          )}
          {/* The Timesheets toggle's look, but each audit is its own address rather than a ?tab= on one. */}
          <div
            className="no-scrollbar inline-flex max-w-full items-stretch overflow-x-auto rounded-md bg-card p-1"
            data-slot="button-group"
            role="group"
          >
            {shownAudits.map(({ value, label }) => (
              <Button
                key={value}
                size="sm"
                aria-pressed={value === tab}
                className={togglerTabButtonClass(value === tab)}
                onClick={() => {
                  if (value === tab) return;
                  const to = pathOf(value);
                  guard(() => transitionNavigate(to, 'fade', { replace: true, state: location.state }));
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    ) : undefined;

  const downloadPdf = (
    <Button variant="outline" size="sm" className="bg-card shadow-xs" onClick={() => window.print()}>
      <Download className="size-4" />
      {t('actions.downloadPdf')}
    </Button>
  );

  const StatusIcon = product.status ? (STATUS_ICON[product.status] ?? Hammer) : null;

  return (
    <ProductViewerProvider viewer={{ isClient: asClient }}>
      <div className="product-report w-full">
        {showBack && (
          <button
            type="button"
            onClick={() => guard(goBack)}
            className="mb-3.5 flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground print:hidden"
          >
            <ChevronLeft className="size-4" />
            {backLabel}
          </button>
        )}

        <div className="mb-4.5 flex flex-wrap items-center gap-3">
          <h1 className={pageTitleClass}>{capitalize(product.name)}</h1>
          <div className="ml-auto flex items-center gap-3">
            {StatusIcon && product.status && (
              <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t(`tabs.${product.status}`)}
                <StatusIcon className="size-4" />
              </span>
            )}
            {/* Offered only where it can succeed: once anybody has filed time, archiving is the way to put it away. */}
            {canEdit && !product.hasReportedTime ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="print:hidden"
                onClick={() => {
                  setRemoveFailed(null);
                  setConfirmingRemove(true);
                }}
              >
                <Trash2 className="size-4" />
                {t('removeProduct.action')}
              </Button>
            ) : null}
          </div>
        </div>

        <TabBar wrapperClassName="mb-4 print:hidden" activeKey={tab}>
          {tabs
            .filter(each => each.shown)
            .map(({ value, label }) => {
              const active = value === tab;
              const to = pathOf(value);
              return (
                <Link
                  key={AUDIT_TABS.includes(value) ? 'audit' : value}
                  to={to}
                  onClick={e => switchTab(e, to, active)}
                  aria-current={active ? 'page' : undefined}
                  className={`${tabClass(active)} inline-flex items-center`}
                >
                  {label}
                </Link>
              );
            })}
        </TabBar>

        {tab === 'settings' && (
          <>
            <ProductDetailsCard product={product} organizationId={organizationId} canEdit={canEdit} />
            {/* Internal by definition, so never on the client's page. */}
            {!asClient && <RndDossierCard product={product} canEdit={canEdit} />}
            {/* Who at the client may read it, and what it is connected to, are ours to set up. */}
            {!asClient && <ClientAccessCard product={product} organizationId={organizationId} />}
            {!asClient && <ConnectedApps product={product} canEdit={canEdit} />}
          </>
        )}

        {/* Only the money tabs read the store, so a failed read takes them and not the page. */}
        {storeFailed && MONEY_TABS.includes(tab) && (
          <p className="text-sm text-muted-foreground">{t('cardReadFailed')}</p>
        )}

        {tab === 'dashboard' && (
          <AsClientReads internal={!asClient}>
            {/* Priced only once the store and the chapters are in: without them it reads in hours, and only the
                money section says it could not load, since an empty chapter list files everyone as unfiled. */}
            {chaptersPending && !storeFailed && <Loader />}
            {(storeFailed || (!chaptersPending && chaptersFailed)) && (
              <ProductDashboard finished={isProductFinished(product.status)} actions={downloadPdf}>
                <p className="text-sm text-muted-foreground">{t('cardReadFailed')}</p>
              </ProductDashboard>
            )}
            {!storeFailed && !chaptersPending && !chaptersFailed && (
              <DashboardWithSpend
                teams={teams}
                chapters={chapters}
                finished={isProductFinished(product.status)}
                actions={downloadPdf}
              >
                <InsightsTab teams={teams} />
              </DashboardWithSpend>
            )}
          </AsClientReads>
        )}

        {tab === 'team' && !storeFailed && (
          <TeamAndRatesTab product={product} organizationId={organizationId} canEdit={canEdit} />
        )}

        {tab === 'sprints' && (
          <AsClientReads internal={!asClient}>
            <ExportDialog
              open={exportOpen}
              onOpenChange={setExportOpen}
              organizationId={organizationId}
              productId={productId}
            />
            <SprintProducts
              actions={
                <Button
                  variant="outline"
                  size="sm"
                  // As tall as the date fields beside it, square once its label is gone.
                  className="bg-card shadow-xs max-sm:size-8 max-sm:px-0"
                  onClick={() => setExportOpen(true)}
                >
                  <Download className="size-4" />
                  {/* Icon-only on a phone, so the dates beside it keep their row. */}
                  <span className="max-sm:sr-only">{t('actions.exportSprints')}</span>
                </Button>
              }
            />
          </AsClientReads>
        )}

        {tab === 'incidents' && (
          <section>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 print:hidden">
              <div>
                <h2 className="text-lg font-bold text-foreground">{t('incidents.productStanding')}</h2>
                <p className="text-sm text-muted-foreground">{t('incidents.productStandingHint')}</p>
              </div>
              <IncidentPeriodSelect value={incidentPeriod} onChange={setIncidentPeriod} productId={product.id} />
            </div>
            {/* A reading, not a door: incidents are logged on the Incidents section, which sees every product at once. */}
            <IncidentsTab
              supportLevel={product.supportLevel}
              period={incidentPeriod}
              canOpen={!asClient}
              showsRetainer={!asClient}
            />
          </section>
        )}

        {/* Only these tabs read the chapters, so a failed read takes the tab and not the page. */}
        {tab === 'costs' && chaptersPending && <Loader />}
        {tab === 'costs' && !chaptersPending && chaptersFailed && (
          <p className="text-sm text-muted-foreground">{t('cardReadFailed')}</p>
        )}
        {!chaptersPending && !chaptersFailed && !storeFailed && (
          <>
            {tab === 'costs' && (
              <CostsTab teams={teams} chapters={chapters} canEdit={canEdit} productName={product.name} />
            )}
          </>
        )}

        {tab === 'monitoring' && (
          <div className="flex flex-col gap-10">
            <HealthSection finished={isProductFinished(product.status)} />
          </div>
        )}

        {tab === 'quality' && (
          <>
            <QualityTab
              productId={productId}
              report={qualityReport}
              isUnavailable={auditsUnavailable}
              header={auditHeader}
              actions={<QualityPeriodSelect value={qualityReport} onChange={setQualityReport} audits={audits} />}
            />
          </>
        )}

        {tab === 'productivity' && (
          <ProductivityTab
            productId={productId}
            report={productivityReport}
            isListLoading={productivityLoading}
            isUnavailable={productivityUnavailable}
            isClient={asClient}
            header={auditHeader}
            actions={
              <QualityPeriodSelect
                value={productivityReport}
                onChange={setProductivityReport}
                audits={productivityReports}
              />
            }
            fallback={
              <AuditReportTab
                productId={productId}
                kind="productivity"
                entry={reportOf('productivity')}
                isListLoading={reportsLoading}
                isUnavailable={reportsUnavailable}
                header={auditHeader}
              />
            }
          />
        )}

        {tab === 'security-audit' && (
          <AuditReportTab
            productId={productId}
            kind="security"
            entry={reportOf('security')}
            isListLoading={reportsLoading}
            isUnavailable={reportsUnavailable}
            header={auditHeader}
          />
        )}

        <RemoveProductDialog
          open={confirmingRemove}
          name={capitalize(product.name) ?? product.name}
          isPending={remove.isPending}
          failed={removeFailed}
          onCancel={() => setConfirmingRemove(false)}
          onConfirm={() =>
            remove.mutate(product.id, {
              onSuccess: () => {
                setConfirmingRemove(false);
                navigate(productsPath(organizationId));
              },
              // A product can gain its first report between this page loading and this click.
              onError: error => {
                const status = (error as { response?: { status?: number } })?.response?.status;
                setRemoveFailed(status === 409 ? t('removeProduct.hasReportedTime') : t('removeProduct.failed'));
              },
            })
          }
        />
      </div>
    </ProductViewerProvider>
  );
}

// The versions hook writes on mount, so nothing renders before the store loads; after a failed read its writes are dropped.
function ProductDetailStored({
  organizationId,
  tab,
  asClient,
}: {
  organizationId: string;
  tab: ProductTab;
  asClient: boolean;
}): ReactElement {
  const { productId } = useParams<{ productId: string }>();
  const store = useProductStore(productId ?? '', asClient);

  if (!store.isReady && !store.isError) return <Loader />;
  return (
    <ProductDetailLoaded
      key={store.revision}
      organizationId={organizationId}
      tab={tab}
      asClient={asClient}
      storeFailed={store.isError}
    />
  );
}

/** One product page for everybody: a client reads what we read, without the controls that change it. */
const ProductDetail = ({
  tab = 'dashboard',
  asClient = false,
}: {
  tab?: ProductTab;
  asClient?: boolean;
}): ReactElement => {
  const organizationId = useOrganizationId();
  if (!organizationId) return <Loader />;
  return (
    <UnsavedWorkProvider>
      <ProductDetailStored organizationId={organizationId} tab={tab} asClient={asClient} />
    </UnsavedWorkProvider>
  );
};

export default ProductDetail;
